
import { useEffect, useRef } from 'react';
import { $getRoot, $getSelection } from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { MarkNode } from '@lexical/mark';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import AdvancedToolbarPlugin from './AdvancedToolbarPlugin';

const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  placeholder: 'text-gray-400',
  paragraph: 'mb-2',
  quote: 'border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4',
  heading: {
    h1: 'text-3xl font-bold mb-4',
    h2: 'text-2xl font-bold mb-3',
    h3: 'text-xl font-bold mb-2',
    h4: 'text-lg font-bold mb-2',
    h5: 'text-base font-bold mb-1',
    h6: 'text-sm font-bold mb-1',
  },
  list: {
    nested: {
      listitem: 'list-none',
    },
    ol: 'list-decimal list-inside mb-2',
    ul: 'list-disc list-inside mb-2',
    listitem: 'mb-1',
  },
  image: 'max-w-full h-auto',
  link: 'text-blue-600 hover:text-blue-800 underline',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    overflowed: 'overflow-hidden',
    hashtag: 'text-blue-500',
    underline: 'underline',
    strikethrough: 'line-through',
    underlineStrikethrough: 'underline line-through',
    code: 'bg-gray-100 px-1 py-0.5 rounded text-sm font-mono',
  },
  code: 'bg-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto mb-4',
  codeHighlight: {
    atrule: 'text-purple-600',
    attr: 'text-blue-600',
    boolean: 'text-red-600',
    builtin: 'text-purple-600',
    cdata: 'text-gray-600',
    char: 'text-green-600',
    class: 'text-blue-600',
    'class-name': 'text-blue-600',
    comment: 'text-gray-500',
    constant: 'text-red-600',
    deleted: 'text-red-600',
    doctype: 'text-gray-600',
    entity: 'text-orange-600',
    function: 'text-purple-600',
    important: 'text-red-600',
    inserted: 'text-green-600',
    keyword: 'text-purple-600',
    namespace: 'text-blue-600',
    number: 'text-red-600',
    operator: 'text-gray-700',
    prolog: 'text-gray-600',
    property: 'text-blue-600',
    punctuation: 'text-gray-700',
    regex: 'text-green-600',
    selector: 'text-green-600',
    string: 'text-green-600',
    symbol: 'text-red-600',
    tag: 'text-red-600',
    url: 'text-blue-600',
    variable: 'text-orange-600',
  },
  table: 'w-full border-collapse border border-gray-300 my-4 overflow-x-auto',
  tableCell: 'border border-gray-300 px-3 py-2 text-sm min-w-[100px]',
  tableCellHeader: 'border border-gray-300 px-3 py-2 bg-gray-50 font-semibold text-sm min-w-[100px]',
};

function Placeholder() {
  return <div className="absolute top-4 left-4 text-gray-400 pointer-events-none">开始编写内容...</div>;
}

// 内容同步组件
function ContentSyncPlugin({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [editor] = useLexicalComposerContext();
  const isInitializedRef = useRef(false);

  // 初始化内容
  useEffect(() => {
    if (!isInitializedRef.current && value) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(value, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
      });
      isInitializedRef.current = true;
    }
  }, [editor, value]);

  // 监听内容变化
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        onChange(htmlString);
      });
    });
  }, [editor, onChange]);

  return null;
}

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export default function RichTextEditor({ 
  value = '', 
  onChange, 
  placeholder = '开始编写内容...',
  className = ''
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'AdvancedRichTextEditor',
    theme,
    onError(error: Error) {
      console.error('Lexical error:', error);
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      MarkNode,
    ],
  };

  return (
    <div className={`relative border border-gray-300 rounded-lg ${className}`}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative">
          <AdvancedToolbarPlugin />
          <div className="relative min-h-[400px] overflow-x-auto">
            <RichTextPlugin
              contentEditable={
                <ContentEditable 
                  className="min-h-[400px] p-4 outline-none resize-none text-gray-900 leading-relaxed focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  style={{ caretColor: '#000' }}
                />
              }
              placeholder={<Placeholder />}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ContentSyncPlugin value={value} onChange={onChange || (() => {})} />
            <HistoryPlugin />
            <AutoFocusPlugin />
            <LinkPlugin />
            <ListPlugin />
            <TablePlugin 
              hasCellMerge={true}
              hasCellBackgroundColor={true}
            />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
}
